<template>
  <!--
    แถบเตือนอายุการใช้งาน — วางบนสุดของ content ทุก layout ที่พนักงานใช้
    ขึ้นทุก role โดยตั้งใจ: พนักงานหน้าร้านทำอะไรไม่ได้ก็จริง แต่เห็นแล้วจะได้ไปบอกเจ้าของ
  -->
  <div
    v-if="license.showBanner"
    class="license-banner"
    :class="toneClass"
    role="alert"
  >
    <i class="fa-solid license-banner__icon" :class="iconClass" />

    <div class="license-banner__body">
      <div class="license-banner__headline">{{ headline }}</div>
      <div v-if="subline" class="license-banner__sub">{{ subline }}</div>
    </div>

    <div class="license-banner__actions">
      <!-- LINE เป็นช่องทางหลัก (เปิดแชตพร้อมข้อความขอต่ออายุ) · โทรเป็นทางรอง -->
      <a
        v-if="contact.line"
        :href="lineHref"
        target="_blank"
        rel="noopener"
        class="license-banner__line"
      >
        <i class="fa-brands fa-line me-1" />{{ $t('_license.Renew via LINE') }}
      </a>
      <a
        v-if="contact.phone"
        :href="telHref"
        class="license-banner__contact"
      >
        <i class="fa-solid fa-phone me-1" />{{ contact.phone }}
      </a>

      <button
        v-if="license.canDismiss"
        type="button"
        class="license-banner__close"
        :aria-label="$t('_license.Dismiss')"
        @click="license.dismissBanner()"
      >
        <i class="fa-solid fa-xmark" />
      </button>
    </div>
  </div>
</template>

<script>
import { useLicenseStore, SUPPORT_CONTACT, lineRenewUrl } from '@/stores/license.store';
import { planLabel, formatLicenseDate } from '@/utils/licenseDisplay';

export default {
  name: 'LicenseBanner',
  computed: {
    license() {
      return useLicenseStore();
    },
    contact() {
      return SUPPORT_CONTACT;
    },
    lineHref() {
      return lineRenewUrl(this.$brand.name || this.$brand.name_en);
    },
    // ตัดขีดออกก่อนใส่ tel: — เบอร์ที่โชว์อ่านง่าย (098-2299881) แต่ที่กดโทรต้องเป็นตัวเลขล้วน
    telHref() {
      return `tel:${String(SUPPORT_CONTACT.phone).replace(/[^0-9+]/g, '')}`;
    },
    // เหลือ <= 3 วัน หรือหมดอายุแล้ว = แดง · นอกนั้นเหลือง
    toneClass() {
      const urgent = this.license.isExpired
        || (this.license.daysLeft !== null && this.license.daysLeft <= 3);
      return urgent ? 'license-banner--danger' : 'license-banner--warn';
    },
    iconClass() {
      return this.license.isExpired ? 'fa-circle-exclamation' : 'fa-triangle-exclamation';
    },
    // มีชื่อแพ็ก = "แพ็ก Starter จะหมดอายุในอีก 2 วัน" · ไม่มี (API เก่า) = ข้อความเดิม
    headline() {
      const plan = planLabel(this.license.planName);
      if (this.license.isExpired) {
        return plan
          ? this.$t('_license.Plan {plan} has expired - please contact support', { plan })
          : this.$t('_license.System has expired - please contact support');
      }
      if (this.license.daysLeft === 0) {
        return plan ? this.$t('_license.Plan {plan} expires today', { plan }) : this.$t('_license.System expires today');
      }
      return plan
        ? this.$t('_license.Plan {plan} expires in {days} days', { plan, days: this.license.daysLeft })
        : this.$t('_license.System expires in {days} days', { days: this.license.daysLeft });
    },
    subline() {
      // ช่วงผ่อนผัน: บอกวันที่จะโดนตัดจริง ให้ร้านรู้ว่ามีเวลาเหลือเท่าไหร่
      if (this.license.isExpired && this.license.lockedOn) {
        return this.$t('_license.System will stop working on {date}', { date: formatLicenseDate(this.license.lockedOn) });
      }
      if (this.license.isExpiring && this.license.expiresOn) {
        return this.$t('_license.Valid until {date}', { date: formatLicenseDate(this.license.expiresOn) });
      }
      return '';
    }
  }
};
</script>

<style scoped>
.license-banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 0.9rem;
  margin-bottom: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid transparent;
  font-size: 0.875rem;
}

.license-banner--warn {
  background: #fff8e1;
  border-color: #ffd54f;
  color: #7a5800;
}

.license-banner--danger {
  background: #fdecea;
  border-color: #f5a3a3;
  color: #8c1c13;
}

.license-banner__icon {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.license-banner__body {
  flex: 1 1 auto;
  min-width: 0;
}

.license-banner__headline {
  font-weight: 700;
}

.license-banner__sub {
  opacity: 0.85;
  font-size: 0.8125rem;
}

.license-banner__actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-shrink: 0;
}

.license-banner__contact {
  font-weight: 700;
  color: inherit;
  text-decoration: none;
  white-space: nowrap;
}

.license-banner__contact:hover {
  text-decoration: underline;
}

/* เขียว LINE ทางการ — เด่นกว่าเบอร์โทรโดยตั้งใจ */
.license-banner__line {
  display: inline-flex;
  align-items: center;
  padding: 0.3rem 0.7rem;
  border-radius: 0.375rem;
  background: #06c755;
  color: #fff;
  font-weight: 700;
  text-decoration: none;
  white-space: nowrap;
}

.license-banner__line:hover {
  background: #05b04b;
  color: #fff;
}

.license-banner__close {
  border: 0;
  background: transparent;
  color: inherit;
  opacity: 0.6;
  line-height: 1;
  padding: 0.25rem;
}

.license-banner__close:hover {
  opacity: 1;
}

@media (max-width: 575.98px) {
  .license-banner {
    flex-wrap: wrap;
  }

  .license-banner__body {
    flex: 1 1 100%;
    order: 2;
  }
}
</style>
